import { useMutation, UseMutationResult } from 'react-query';

import { verifyEmail, verifyUser } from 'src/utils/urls';
import { UserCodeverificationType } from '../types/apiTypes';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

export type TResult<TData = unknown> = {
  Errors: ApiErrorItem[];
  ErrorId: string;
  IsSuccess: boolean;
  StatusCode: number;
  Data?: TData;
};

export type TArguments = UserCodeverificationType;
export type TVariables = UserCodeverificationType;

export type TError = Error;

export async function verifyUserDetails({
  id,
  type,
  code,
}: TArguments): Promise<TResult> {
  const response = await fetch(
    type ? verifyUser(id, code) : verifyEmail(id, code),
    {
      method: 'POST',
      headers: {
        tenant: process.env.REACT_APP_TENANT_ID || '',
        'Content-Type': 'application/json',
      },
    },
  );

  let data: TResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useUserVerification(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  unknown
> {
  return useMutation(async ({ id, type, code }: TVariables) => {
    if (!code) {
      throw new Error('Verification code is required.');
    }

    const result = await verifyUserDetails({ id, type, code });

    if (!result.IsSuccess) {
      const errorMessage =
        result.Errors?.map(err => err.Message).join(', ') ||
        'Verification failed.';

      throw new Error(errorMessage);
    }

    return result;
  });
}

export { useUserVerification };
